import { RADIUS, SPACE, T } from '@/design/tokens';
import { trackBoundaryError } from '@/lib/telemetry';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Route render errors to the telemetry error stream (fire-and-forget).
    // trackBoundaryError scrubs PII and swallows all errors internally.
    trackBoundaryError(error);

    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div
        role="alert"
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          background: T.bg,
          color: T.text,
          padding: SPACE.lg,
        }}
      >
        <div
          style={{
            maxWidth: 480,
            textAlign: 'center',
            background: T.card,
            border: `1px solid ${T.border}`,
            borderLeft: `3px solid ${T.red}`,
            borderRadius: RADIUS.md,
            padding: SPACE.lg,
          }}
        >
          <h1 style={{ margin: 0, fontWeight: 400, fontSize: 22 }}>Something broke</h1>
          <p style={{ color: T.textMuted, marginTop: SPACE.md }}>
            We hit an error rendering this page. The garage is still open. Try again.
          </p>
          <div
            style={{
              marginTop: SPACE.lg,
              display: 'flex',
              gap: SPACE.md,
              justifyContent: 'center',
            }}
          >
            <Button onClick={this.reset}>Try again</Button>
            <Button variant="secondary" as="a" href="/">
              Home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
